import React, { useState, useCallback } from "react";
import {
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import moment from "moment";
import ThreeDotsGreenIcon from "../../../../../assets/images/ico_more_options_green.svg";
import ThreeDotsWhiteIcon from "../../../../../assets/images/ico_more_options_web.svg";
import HolidayIcon from "../../../../../assets/images/early_on.svg";
import ViewDetailsModal from "../../modals/ViewDetailsModal";
import constants from "../../../../../constants";
import FamilyModal from "patient-scheduling/pages/FamilyMembers/components/AddedMembers/FamilyModal";
import { updateTimesheetStatus } from "repositories/timesheet-repository";
import toast from "react-hot-toast";
import RejectionModal from "staff/pages/StaffListingTimesheet/component/RejectionModal";
import styles from "../../StaffAttendanceTable.module.scss";
import "../../modals/StatusDetails.scss";
import Empty from "components/Empty";

const StaffAttendanceTable = ({
  checkedColumns,
  onCheckboxChange,
  dateHeaders,
  staffListTimesheet,
  holidays,
  t,
  refetch,
  setShowLoader,
  showLoader,
  selectedTimesheetStatuses,
  timesheetListingStatus: timesheetListingStatusProp,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedModalData, setSelectedModalData] = useState(null);
  const timesheetListingStatus =
    timesheetListingStatusProp || constants.TimesheetListingStatus;

  const [selectedTimesheetIds, setSelectedTimesheetIds] = useState([]);
  const [confirmModal, setConfirmModal] = useState({ open: false, type: null });
  const [isRejectionModalOpen, setIsRejectionModalOpen] = useState(false);
  const [selectMemberName, setSelectMemberName] = useState("");

  const toggleModal = () => setIsModalOpen((prev) => !prev);

  const openModalWithData = useCallback((staff, date, timesheets) => {
    setSelectedModalData({ staff, date, timesheets });
    setIsModalOpen(true);
  }, []);

  // Get all timesheets for a specific date
  const getTimesheetsForDate = useCallback((timesheets, date) => {
    const dateStr = moment(date).format("YYYY-MM-DD");
    return timesheets?.filter((timesheet) => {
      const timesheetDate = moment(timesheet.date).format("YYYY-MM-DD");
      return timesheetDate === dateStr;
    });
  }, []);

  // Get holiday for a specific date
  const getHolidayForDate = useCallback(
    (date) => {
      const headerDate = moment(date).format("YYYY-MM-DD");
      return holidays.get(headerDate);
    },
    [holidays]
  );

  const updateStatus = useCallback(
    async (statusId, reason) => {
      if (showLoader) return;
      setShowLoader && setShowLoader(true);
      try {
        const response = await updateTimesheetStatus({
          timesheetId: selectedTimesheetIds?.toString(),
          statusId,
          reason: reason || "",
        });
        toast.success(response?.message);
        refetch && refetch();
      } catch (error) {
        toast.error(error?.message);
      } finally {
        setShowLoader && setShowLoader(false);
        setSelectedTimesheetIds([]);
      }
    },
    [selectedTimesheetIds, showLoader]
  );

  // Check if date is in leave range
  const isDateInLeaveRange = useCallback((date, leaves) => {
    if (!leaves || leaves.length === 0) return false;

    return leaves.some((leave) => {
      const leaveStartDate = moment(leave.startDate).startOf("day");
      const leaveEndDate = moment(leave.endDate).startOf("day");
      const checkDate = moment(date).startOf("day");

      return checkDate.isBetween(leaveStartDate, leaveEndDate, "day", "[]");
    });
  }, []);

  // Format time spent (minutes to hours with decimal)
  const formatTimeSpent = useCallback((minutes) => {
    if (!minutes) return "-";
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    const decimal = (mins / 60).toFixed(2).substring(1);
    return `${hours}${decimal}`;
  }, []);

  // Convert minutes to hours for total
  const convertMinutesToHours = useCallback((minutes) => {
    if (!minutes) return "-";
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    const decimal = (mins / 60).toFixed(2).substring(1);
    return `${hours}${decimal} Hrs`;
  }, []);

  // Check if current day
  const isCurrentDay = useCallback((date) => {
    return moment(date).isSame(moment(), "day");
  }, []);

  // Get status cell styling based on timesheets
  const getStatusCellStyle = useCallback(
    (timesheets, date, staff, holidayColor) => {
      const isLeaveDay = isDateInLeaveRange(date, staff?.leaves);

      // If it's a holiday, use holiday color
      if (holidayColor) {
        return {
          backgroundColor: holidayColor,
          icon: ThreeDotsGreenIcon,
        };
      }

      // If there are timesheets (after filtering), prioritize timesheet status over leave
      // This ensures that when a timesheet status filter is applied, we show timesheet status
      if (timesheets && timesheets.length > 0) {
        // Get all unique statusIds from timesheets
        const statusIds = timesheets.map((ts) => ts.statusId).filter(Boolean);
        const uniqueStatusIds = [...new Set(statusIds)];

        // If mixed status (different statusIds)
        if (uniqueStatusIds.length > 1) {
          return {
            backgroundColor: "#ECF1FD", // Mixed color
            icon: ThreeDotsGreenIcon,
          };
        }

        // All timesheets have same statusId
        const statusId = uniqueStatusIds[0];

        switch (statusId) {
          case timesheetListingStatus[1]?.value: // Pending Approval
            return {
              // Align with legend: Pending = #FEF3D7
              backgroundColor: "#FEF3D7",
              icon: ThreeDotsGreenIcon,
            };
          case timesheetListingStatus[2]?.value: // Approved
            return {
              // Align with legend: Approved = #F9FCEE
              backgroundColor: "#F9FCEE",
              icon: ThreeDotsGreenIcon,
            };
          case timesheetListingStatus[3]?.value: // Rejected
            return {
              backgroundColor: "#EE4F4F",
              color: "#ffffff",
              icon: ThreeDotsWhiteIcon,
            };
          case timesheetListingStatus[4]?.value: // Paid
            return {
              backgroundColor: "#A9CF3D",
              icon: ThreeDotsGreenIcon,
            };
          default:
            return {
              backgroundColor: undefined,
              icon: ThreeDotsGreenIcon,
            };
        }
      }

      // If no timesheets, check for leave day
      // Only show leave color if no timesheet status filter is applied (or "mixed" is selected)
      if (isLeaveDay) {
        return {
          backgroundColor: "#FCC539",
          icon: ThreeDotsGreenIcon,
        };
      }

      // If no timesheets and not a leave day, return default
      return {
        backgroundColor: undefined,
        icon: ThreeDotsGreenIcon,
      };
    },
    [timesheetListingStatus, isDateInLeaveRange]
  );

  // Filter timesheets by selected status
  const filterTimesheetsByStatus = useCallback(
    (timesheets) => {
      // If no filter is applied, return all timesheets
      if (
        !selectedTimesheetStatuses ||
        selectedTimesheetStatuses.length === 0
      ) {
        return timesheets;
      }

      if (!timesheets || timesheets.length === 0) {
        return timesheets;
      }

      // Map filter values to actual status values
      const statusValueMap = {
        pending: Number(timesheetListingStatus[1]?.value), // 2
        approved: Number(timesheetListingStatus[2]?.value), // 3
        rejected: Number(timesheetListingStatus[3]?.value), // 4
        paid: Number(timesheetListingStatus[4]?.value), // 5
      };

      // Get the status values for selected filters (excluding "mixed")
      // When multiple statuses are selected (e.g., ["rejected", "pending"]),
      // this will create an array of their numeric values (e.g., [4, 2])
      const selectedStatusValues = selectedTimesheetStatuses
        .filter((status) => status !== "mixed")
        .map((status) => statusValueMap[status])
        .filter((val) => !isNaN(val) && val !== null && val !== undefined);

      // Check if "Mixed" is selected
      const isMixedSelected = selectedTimesheetStatuses.includes("mixed");

      // Get unique statusIds from timesheets
      const statusIds = timesheets
        .map((ts) => Number(ts.statusId))
        .filter((id) => !isNaN(id) && id !== null && id !== undefined);
      const uniqueStatusIds = [...new Set(statusIds)];
      const isMixed = uniqueStatusIds.length > 1;

      // If only "Mixed" is selected (no other statuses), only show timesheets if this cell has multiple unique statuses
      if (isMixedSelected && selectedStatusValues.length === 0) {
        // Show all timesheets if the cell has mixed statuses (multiple different statuses)
        return isMixed ? timesheets : [];
      }

      // If "Mixed" is selected along with other statuses
      if (isMixedSelected && selectedStatusValues.length > 0) {
        // Show timesheets that match selected statuses
        // If the cell has mixed statuses AND at least one matches, show all timesheets in that cell
        const hasMatchingStatus = uniqueStatusIds.some((statusId) =>
          selectedStatusValues.includes(statusId)
        );
        if (isMixed && hasMatchingStatus) {
          // If cell has mixed statuses and at least one matches, show all timesheets
          return timesheets;
        }
        // Otherwise, only show timesheets matching selected statuses
        return timesheets.filter((ts) => {
          const statusId = Number(ts.statusId);
          return selectedStatusValues.includes(statusId);
        });
      }

      // If only specific statuses are selected (no "Mixed"), filter by those statuses
      if (selectedStatusValues.length === 0) {
        return [];
      }

      // Filter timesheets to only include those matching ANY of the selected statuses
      // When multiple statuses are selected (e.g., "Rejected" and "Pending"),
      // show all timesheets that match ANY of those statuses
      return timesheets.filter((ts) => {
        const statusId = Number(ts.statusId);
        // Check if this timesheet's statusId matches any of the selected status values
        return selectedStatusValues.includes(statusId);
      });
    },
    [selectedTimesheetStatuses, timesheetListingStatus]
  );

  // Calculate total time spent for a staff member
  // When timesheet status filter is applied, only include filtered timesheets
  const calculateTotalTimeSpent = useCallback(
    (staff) => {
      if (!staff?.timesheets || staff.timesheets.length === 0) return 0;

      // If timesheet status filter is applied, filter timesheets first
      let timesheetsToCalculate = staff.timesheets;
      if (selectedTimesheetStatuses && selectedTimesheetStatuses.length > 0) {
        timesheetsToCalculate = filterTimesheetsByStatus(staff.timesheets);
      }

      return timesheetsToCalculate.reduce(
        (total, ts) => total + (ts.timeSpent || 0),
        0
      );
    },
    [selectedTimesheetStatuses, filterTimesheetsByStatus]
  );

  // Render cell content for a specific date and staff
  const renderCell = useCallback(
    (date, staff) => {
      let timesheets = getTimesheetsForDate(staff.timesheets, date);

      // Filter timesheets by selected status
      timesheets = filterTimesheetsByStatus(timesheets);

      const holiday = getHolidayForDate(date);
      const holidayColor = holiday ? "#d0d7d9" : undefined;
      const isLeaveDay = isDateInLeaveRange(date, staff?.leaves);
      const staffWorkingDays = staff?.user?.workingDays
        ? staff.user?.workingDays.split(",").map(Number)
        : [];
      const weekday = moment(date).isoWeekday();
      const isWorkingDay = staffWorkingDays.includes(weekday);

      // Check if timesheet status filter is applied (excluding "mixed")
      const isTimesheetStatusFilterApplied =
        selectedTimesheetStatuses &&
        selectedTimesheetStatuses.length > 0 &&
        !selectedTimesheetStatuses.includes("mixed");

      // If no timesheets after filtering
      if (!timesheets || timesheets.length === 0) {
        // If timesheet status filter is applied (not mixed), don't show leave records
        if (isTimesheetStatusFilterApplied && isLeaveDay) {
          return (
            <td className={styles.cell}>
              <p className="m-0">-</p>
            </td>
          );
        }

        return (
          <td
            className={styles.cell}
            style={{
              backgroundColor: holidayColor
                ? holidayColor
                : isLeaveDay
                ? "#FCC539"
                : isWorkingDay
                ? "#ECF1FD"
                : undefined,
            }}
          >
            <p className="m-0">
              {isWorkingDay && !isLeaveDay ? "No timesheet added" : "-"}
            </p>
          </td>
        );
      }

      // Sum timeSpent for all filtered timesheets on this date
      const totalTimeSpent = timesheets.reduce(
        (sum, ts) => sum + (ts.timeSpent || 0),
        0
      );

      if (!totalTimeSpent || totalTimeSpent === 0) {
        return (
          <td className={styles.cell} style={{ background: holidayColor }}>
            <p className="m-0">-</p>
          </td>
        );
      }

      const timeDisplay = formatTimeSpent(totalTimeSpent);
      const statusStyle = getStatusCellStyle(
        timesheets,
        date,
        staff,
        holidayColor
      );

      // Build menu options based on combined timesheet statuses for the date
      const menuOptions = [];

      const hasApproved = timesheets.some(
        (ts) => ts?.statusId === timesheetListingStatus[2]?.value
      );
      const hasPending = timesheets.some(
        (ts) => ts?.statusId === timesheetListingStatus[1]?.value
      );

      // If any Approved exists, allow Mark as Paid
      if (hasApproved) {
        menuOptions.push({
          text: "Mark as Paid",
          onclick: () => {
            const ids = timesheets
              .filter((t) => t?.statusId === timesheetListingStatus[2]?.value)
              .map((t) => t.id);
            setSelectedTimesheetIds(ids);
            setConfirmModal({ open: true, type: "paid" });
          },
        });
      }

      // If any Pending For Approval exists, show Approve and Reject
      if (hasPending) {
        menuOptions.push({
          text: "Approve",
          onclick: () => {
            const ids = timesheets
              .filter((t) => t?.statusId === timesheetListingStatus[1]?.value)
              .map((t) => t.id);
            setSelectedTimesheetIds(ids);
            setConfirmModal({ open: true, type: "approve" });
          },
        });
        menuOptions.push({
          text: "Reject",
          onclick: () => {
            const ids = timesheets
              .filter((t) => t?.statusId === timesheetListingStatus[1]?.value)
              .map((t) => t.id);
            setSelectedTimesheetIds(ids);
            setSelectMemberName(
              `${staff?.user?.firstName || ""} ${staff?.user?.lastName || ""}`
            );
            setIsRejectionModalOpen(true);
          },
        });
      }

      // Always provide View Details
      menuOptions.push({
        text: "View Details",
        onclick: () => openModalWithData(staff, date, timesheets),
      });

      return (
        <td
          className={`${styles.cell}`}
          style={{
            backgroundColor: statusStyle.backgroundColor,
          }}
        >
          <div className="d-flex justify-content-between align-items-center">
            <p
              className={styles.cell_text}
              style={{
                color:
                  statusStyle.backgroundColor === "#EE4F4F"
                    ? "#ffffff"
                    : undefined,
              }}
            >
              {timeDisplay}
            </p>
            {menuOptions.length > 0 && (
              <UncontrolledDropdown>
                <DropdownToggle
                  caret={false}
                  tag="div"
                  className="cursor-pointer"
                >
                  <img src={statusStyle.icon} alt="menu" />
                </DropdownToggle>
                <DropdownMenu end className={styles["office-dropdown-menu"]}>
                  {menuOptions.map((option, index) => (
                    <DropdownItem
                      key={index}
                      className={styles["office-dropdown-item"]}
                      onClick={option.onclick}
                    >
                      {option.text}
                    </DropdownItem>
                  ))}
                </DropdownMenu>
              </UncontrolledDropdown>
            )}
          </div>
        </td>
      );
    },
    [
      getTimesheetsForDate,
      filterTimesheetsByStatus,
      getHolidayForDate,
      isDateInLeaveRange,
      formatTimeSpent,
      getStatusCellStyle,
      timesheetListingStatus,
      toggleModal,
      openModalWithData,
      selectedTimesheetStatuses,
    ]
  );

  if (!staffListTimesheet || staffListTimesheet.length === 0) {
    return (
      <div className={styles.tableContainer}>
        <Empty Message={t("noDataAvaliableForSelectoion")} />
      </div>
    );
  }

  return (
    <div className={styles.tableContainer}>
      <table className={styles.attendanceTable}>
        <thead>
          <tr>
            <th className={styles.heading}>Name</th>
            {dateHeaders.map((dateHeader, i) => (
              <th
                key={i}
                className={`${styles.heading} ${
                  dateHeader.isHoliday ? styles.holidayColumnHeader : ""
                }`}
                style={{
                  backgroundColor: isCurrentDay(dateHeader.date)
                    ? "#66accf"
                    : "#f8f9f7",
                }}
              >
                <div className="ch-checkbox">
                  <label className="m-0">
                    <input
                      type="checkbox"
                      checked={!!checkedColumns[dateHeader.date]}
                      onChange={() => onCheckboxChange(dateHeader.date)}
                    />
                    <span
                      style={{
                        fontSize: "12px",
                        textTransform: "capitalize",
                        color: isCurrentDay(dateHeader.date)
                          ? "#f8f9f7"
                          : undefined,
                      }}
                    >
                      {dateHeader.label}
                    </span>
                  </label>
                </div>
              </th>
            ))}
            <th className={styles.heading}>Total</th>
          </tr>
        </thead>
        <tbody>
          {/* Holiday row */}
          {dateHeaders.some((dh) => dh.isHoliday) && (
            <tr className={styles.holidayRow}>
              <td className={styles.emptyHolidayCell}></td>
              {dateHeaders.map((dateHeader, index) => {
                const holiday = getHolidayForDate(dateHeader.date);
                return (
                  <td
                    key={index}
                    className={
                      holiday ? styles.holidayCell : styles.emptyHolidayCell
                    }
                    style={{
                      background: holiday ? "#587E85" : "#FFFFFF",
                      color: holiday ? "#FFFFFF" : undefined,
                    }}
                  >
                    {holiday ? (
                      <div className={styles.holiday_text_icon_wrapper}>
                        <img src={HolidayIcon} alt="holiday" />
                        <p className={styles.holiday_text}>
                          {holiday.title.toUpperCase()}
                        </p>
                      </div>
                    ) : (
                      ""
                    )}
                  </td>
                );
              })}
              <td className={styles.emptyHolidayCell}></td>
            </tr>
          )}

          {/* Staff rows */}
          {staffListTimesheet.map((staff, idx) => {
            const totalTimeSpent = calculateTotalTimeSpent(staff);

            return (
              <tr key={staff.user?.id || idx}>
                <td className={styles.name}>
                  <span className={styles.name_span}>
                    {`${staff?.user?.firstName || ""} ${
                      staff?.user?.lastName || ""
                    }`}
                  </span>
                </td>

                {dateHeaders.map((dateHeader, i) => (
                  <React.Fragment key={i}>
                    {renderCell(dateHeader.date, staff)}
                  </React.Fragment>
                ))}

                <td className={styles.total}>
                  {convertMinutesToHours(totalTimeSpent)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {confirmModal.open && (
        <FamilyModal
          isFamilyModalOpen={confirmModal.open}
          setIsFamilyModalOpen={(val) =>
            setConfirmModal((prev) => ({ ...prev, open: val }))
          }
          title={
            confirmModal.type === "approve"
              ? t("staffAttendance.approveTimesheet")
              : confirmModal.type === "paid"
              ? t("staffAttendance.markTimesheetAsPaid")
              : ""
          }
          subTitle1={
            confirmModal.type === "approve"
              ? t(
                  "staffAttendance.onlyTimesheetsWithPendingStatusWillBeApproved"
                )
              : t(
                  "staffAttendance.onlyTimesheetsWithAcceptedStatusWillBeMarkedAsPaid"
                )
          }
          leftBtnText={
            confirmModal.type === "approve"
              ? t("staffAttendance.approve")
              : t("staffAttendance.markAsPaid")
          }
          rightBtnText={t("cancel")}
          onConfirm={() => {
            const statusId =
              confirmModal.type === "approve"
                ? timesheetListingStatus[2]?.value
                : timesheetListingStatus[4]?.value;
            setConfirmModal({ open: false, type: null });
            updateStatus(statusId);
          }}
        />
      )}
      {isRejectionModalOpen && (
        <RejectionModal
          isRejectionModalOpen={isRejectionModalOpen}
          setIsRejectionModalOpen={setIsRejectionModalOpen}
          staffMemberName={selectMemberName}
          hideSelectedEmployee={true}
          titleOverride={t("staffAttendance.rejectTimesheet")}
          confirmLabelOverride={t("staffAttendance.reject")}
          confirmMessage={t(
            "staffAttendance.areYouSureYouWantToRejectTheTimesheet"
          )}
          leadMessage={t(
            "staffAttendance.onlyTimesheetsWithPendingStatusWillBeRejected"
          )}
          centerTitle={true}
          onReject={(reason) => {
            setIsRejectionModalOpen(false);
            updateStatus(timesheetListingStatus[3]?.value, reason);
          }}
        />
      )}
      {selectedModalData && (
        <ViewDetailsModal
          isOpen={isModalOpen}
          toggle={() => {
            toggleModal();
            setSelectedModalData(null);
          }}
          staff={selectedModalData.staff}
          date={selectedModalData.date}
          t={t}
          refetch={refetch}
          showLoader={showLoader}
          setShowLoader={setShowLoader}
        />
      )}
    </div>
  );
};

export default StaffAttendanceTable;
